/**
 * Add User Form Composer
 *
 * Form for creating an active user directly (no invitation — see inviteUser for that).
 * - Multi-select for job roles
 * - Multi-select for organizations
 * - Profile picture upload (optional)
 * - userStatus is not sent on create; the backend defaults new users to 'active'
 *   (there is no 'invitationPending' status)
 *
 * Form metadata and field configurations are loaded from form.yml
 */

import { useAppForm } from '@/hooks/app-form'
import { useApiMutation, useApiQuery } from '@/api/ApiQueryServiceProvider'
import {
  AddUserFormSchema,
  transformToApiRequest,
  FORM_CONFIG,
  type AddUserFormData,
} from './schema'
import { FormConfigLoader } from '@/lib/formConfigLoader'
import {
  renderTextControl,
  renderButtonControl,
  renderSelectControl,
  type ControlConfig
} from '@/forms/formRenderFunctions'
import { useSelectOptionsMap } from '@/hooks/useSelectOptions'
import { coerceFieldValue, validateBackendPhone } from '@/lib/utils'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { AppLogger } from '@/lib/logger'
import { useToast } from '@/contexts/ToastContext'
import { renderMediaUploaderControl } from '@/forms/formRenderFunctions'
import { PhoneInputField } from '@/components/ui/phone-input'
import { useConfig } from '@/contexts/ConfigContext'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card'
import { UserPlus } from 'lucide-react'

const log = AppLogger.getLogger(import.meta)

// ============================================================================
// Component Props
// ============================================================================

export type AddUserComposerProps = {
  onSuccess?: () => void
  onError?: (error: Error) => void
  onCancel?: () => void
  initialValues?: Partial<AddUserFormData>
  showHeader?: boolean
  submitLabel?: string
}

// ============================================================================
// Field validation via Zod schema (onBlur only)
// ============================================================================

const validateField = (name: keyof AddUserFormData, value: unknown): string | null => {
  const shape = AddUserFormSchema.shape[name]
  if (!shape) return null

  const parsed = shape.safeParse(value)
  if (!parsed.success) {
    return parsed.error.issues[0]?.message ?? 'Invalid value'
  }
  return null
}

// Backend enforces UNIQUE(user_name) with a 30-char max. userName is derived from the
// unique email so first/last display names may repeat without colliding on user_name.
const USERNAME_MAX = 30

// ============================================================================
// Composer Component
// ============================================================================

export function AddUserComposer(props: AddUserComposerProps) {
  const { onSuccess, onError, onCancel, initialValues, showHeader = true, submitLabel } = props
  const { toast } = useToast()
  const config = useConfig()

  // Get field configurations from YAML
  const controlsByGroup = FormConfigLoader.getControlsByGroup(FORM_CONFIG)
  const groups = FormConfigLoader.getGroups(FORM_CONFIG)

  const isRequiredField = (name: string) => {
    return ['firstName', 'lastName', 'emailAddress'].includes(name)
  }

  // Fetch all select options with a single hook call
  const { optionsMap, loadingMap } = useSelectOptionsMap({
    organizations: {
      endpoint: 'getOrganizations',
      dataKey: 'organizations',
      valueField: 'orgNumber',
      labelField: 'name',
    },
  })

  // Existing users — used to block creating a duplicate email. The backend upserts by email,
  // so submitting an existing email would silently UPDATE that user instead of erroring.
  const { data: existingUsersData } = useApiQuery('getUsers', {}, {})

  const upsertUser = useApiMutation('upsertUser', 'POST', { invalidates: ['getUsers', 'getUserByEmail'] })
  // Org association now goes through memberships (backend v1.0.36 retired user_organizations).
  // Note: the backend rejects a membership when the org's Farmgrid account is INACTIVE
  // (409 FARMGRID_ACCOUNT_INACTIVE) — the default for every new org; `active` and `unpaid`
  // are allowed. Invalidate getUserByNumber too so the user's Organizations card refreshes.
  const addOrgMembershipMutation = useApiMutation('upsertOrganizationMembership', 'POST', { invalidates: ['getUsers', 'getUserByEmail', 'getUserByNumber', 'getMemberships'] })

  const form = useAppForm({
    schema: AddUserFormSchema,
    defaultValues: {
      firstName: initialValues?.firstName ?? '',
      lastName: initialValues?.lastName ?? '',
      emailAddress: initialValues?.emailAddress ?? '',
      phone: initialValues?.phone ?? '',
      organizations: initialValues?.organizations ?? [],
      tenants: (initialValues as any)?.tenants ?? ['conex'],
      profilePicture: initialValues?.profilePicture ?? '',
    },
    onSubmit: async ({ value }) => {
      const formValue = value as AddUserFormData
      // Coerce and validate with Zod schema (single source of truth)
      const coerced = {
        firstName: formValue.firstName?.trim() ?? '',
        lastName: formValue.lastName?.trim() ?? '',
        emailAddress: formValue.emailAddress?.trim() ?? '',
        phone: formValue.phone || undefined,
        organizations: Array.isArray(formValue.organizations) ? formValue.organizations : [],
        tenants: Array.isArray(formValue.tenants) && formValue.tenants.length > 0 ? formValue.tenants : ['conex'],
        // Use uploaded file objectKey or existing value
        profilePicture: formValue.profilePicture || undefined,
      }

      const parsed = AddUserFormSchema.safeParse(coerced)

      if (!parsed.success) {
        for (const issue of parsed.error.issues) {
          const fieldName = issue.path[0]
          if (typeof fieldName === 'string') {
            form.setFieldMeta(fieldName as keyof AddUserFormData, (meta) => ({
              ...meta,
              errors: [issue.message],
            }))
          }
        }
        log.error('[AddUserComposer] Validation failed:', parsed.error)
        return
      }

      // Backend builds userName as `${firstName} ${lastName}` and validates it as
      // 3-30 chars. firstName/lastName are each max 20, so the combined value can
      // exceed 30 — guard here so it never reaches the backend (schema untouched).
      const userName = `${parsed.data.firstName} ${parsed.data.lastName}`
      if (userName.length < 3 || userName.length > 30) {
        const target: keyof AddUserFormData =
          parsed.data.lastName.length >= parsed.data.firstName.length ? 'lastName' : 'firstName'
        form.setFieldMeta(target, (meta) => ({
          ...meta,
          isTouched: true,
          errors: ['First and last name together must be 3-30 characters'],
        }))
        toast({
          title: 'Name too long',
          description: 'First and last name together must be between 3 and 30 characters.',
          type: 'error',
        })
        return
      }

      // Phone must satisfy the backend mobileNumber/countryCode contract.
      const phoneError = validateBackendPhone(parsed.data.phone)
      if (phoneError) {
        form.setFieldMeta('phone', (meta) => ({ ...meta, isTouched: true, errors: [phoneError] }))
        return
      }

      // Reject duplicate EMAIL before submitting — email is the unique identity for a
      // user (names may legitimately repeat). The backend enforces UNIQUE(email_address)
      // and would otherwise return a raw 500 (duplicate key) instead of a clear message.
      const existingUsers =
        ((existingUsersData as any)?.content?.users ?? []) as Array<{ emailAddress?: string; userName?: string }>
      const emailExists = existingUsers.some(
        (u) => (u.emailAddress ?? '').trim().toLowerCase() === parsed.data.emailAddress.trim().toLowerCase()
      )
      if (emailExists) {
        form.setFieldMeta('emailAddress', (meta) => ({
          ...meta,
          isTouched: true,
          errors: ['A user with this email already exists'],
        }))
        toast({
          title: 'User already exists',
          description: 'A user with this email address already exists.',
          type: 'error',
        })
        return
      }

      // First/last names may repeat freely — email is the unique identity. The backend
      // enforces UNIQUE(user_name), but the getUsers list does NOT return userName, so we
      // can't dedupe against it. Instead derive a guaranteed-unique userName from the unique
      // email (capped at the backend's 30-char limit) so duplicate display names never
      // collide on user_name.
      const uniqueUserName = parsed.data.emailAddress.trim().slice(0, USERNAME_MAX)

      try {
        const request = transformToApiRequest(parsed.data, uniqueUserName)
        const selectedOrganizations = parsed.data.organizations || []

        // Create/update the user
        const userResponse = await upsertUser.mutateAsync(request)

        // Extract userNumber from response (API returns { message, content: { user: { userNumber, ... } } })
        // Coerce to number — backend may return it as a string
        const userNumber = userResponse?.content?.user?.userNumber != null
          ? Number(userResponse.content.user.userNumber)
          : undefined

        // The user was created but the backend didn't return a userNumber — we can't map
        // the organization, so the operation cannot fully succeed. Treat it as a failure.
        if (!userNumber) {
          throw new Error('User was created but no userNumber was returned; organization could not be assigned.')
        }

        // Job roles were removed from the invite flow (legacy job-role removal). A user's
        // product access is granted via account memberships on the user detail page instead.

        // Associate organizations via memberships (NON-FATAL): the user has already been
        // created, so a membership failure (commonly 409 FARMGRID_ACCOUNT_INACTIVE — the
        // default for a new org) must NOT roll the invite back into an error — otherwise the
        // admin hits the duplicate-email guard on retry. Collect failures and surface them as
        // a warning, but still report success. Body is { userNumber } only (orgNumber is the
        // path param; no roleNumber in v1.0.36).
        const membershipFailures: string[] = []
        if (selectedOrganizations.length > 0) {
          const results = await Promise.allSettled(
            selectedOrganizations.map((orgNumber) =>
              addOrgMembershipMutation.mutateAsync({
                orgNumber: parseInt(orgNumber, 10),
                userNumber,
              })
            )
          )
          results.forEach((r, i) => {
            if (r.status === 'rejected') {
              const orgValue = selectedOrganizations[i]
              const orgLabel = optionsMap.organizations?.find((o) => o.value === orgValue)?.label ?? `Org ${orgValue}`
              membershipFailures.push(orgLabel)
              log.warn(`[AddUserComposer] Membership failed for ${orgLabel}:`, (r.reason as Error)?.message)
            }
          })
        }

        if (membershipFailures.length > 0) {
          toast({
            title: 'User created',
            description: `Couldn't add them to ${membershipFailures.join(', ')} — the organization's FarmGrid account is inactive. Add the membership from the user's detail page instead.`,
            type: 'warning',
            duration: 6000,
          })
        }

        // The user was created — report success even if some memberships failed.
        onSuccess?.()
      } catch (error) {
        log.error('[AddUserComposer] Submission error:', error)
        // Surface a clear message instead of leaving the user with only a console error.
        // The backend returns a generic 500 for duplicate keys (e.g. a userName collision
        // if the users list was stale); email is guarded above, so hint at that path.
        toast({
          title: 'Could not add user',
          description:
            'Something went wrong creating the user. If this keeps happening, the email may already be in use — try a different email.',
          type: 'error',
        })
        onError?.(error as Error)
      }
    },
  })

  // Render a single text control
  const renderTextControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onChange: ({ value }) => {
            // Skip validation for empty optional fields
            if (!isRequired && (!value || value === '')) return undefined
            // Don't coerce mobile/country code fields to numbers - they're strings
            const coercedValue = ['mobileNumber', 'countryCode'].includes(control.name)
              ? value
              : coerceFieldValue(control.name, value)
            return validateField(
              control.name as keyof AddUserFormData,
              coercedValue
            ) || undefined
          },
          onBlur: ({ value }) => {
            // Don't coerce mobile/country code fields to numbers - they're strings
            const coercedValue = ['mobileNumber', 'countryCode'].includes(control.name)
              ? value
              : coerceFieldValue(control.name, value)
            return validateField(
              control.name as keyof AddUserFormData,
              coercedValue
            ) || undefined
          },
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            type: control.inputType,
          }
          return renderTextControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render a single select control
  const renderSelectControlField = (control: any) => {
    const isRequired = isRequiredField(control.name)
    const isLoading = loadingMap[control.name]

    // Get options from API or YAML
    const options = control.optionsEndpoint
      ? optionsMap[control.name] || []
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)

    return (
      <form.AppField
        key={control.name}
        name={control.name}
        validators={{
          onChange: ({ value }) =>
            validateField(
              control.name as keyof AddUserFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
          onBlur: ({ value }) =>
            validateField(
              control.name as keyof AddUserFormData,
              coerceFieldValue(control.name, value)
            ) || undefined,
        }}
      >
        {(bound) => {
          const config: ControlConfig = {
            name: control.name,
            label: control.label,
            placeholder: control.placeholder,
            required: isRequired,
            visibility: control.visibility ?? true,
            disabled: control.disabled ?? false,
            options: options,
            isLoading: isLoading,
          }
          return renderSelectControl(config, bound)
        }}
      </form.AppField>
    )
  }

  // Render multi-select control (using checkboxes)
  const renderMultiSelectControlField = (control: any) => {
    const options = control.optionsEndpoint
      ? (optionsMap[control.name] || [])
      : FormConfigLoader.normalizeSelectOptions(control.selectOptions)
    const isLoading = loadingMap[control.name]

    return (
      <form.AppField
        key={control.name}
        name={control.name}
      >
        {(bound) => {
          const currentValue = (bound.state.value as string[]) || []

          const handleCheckboxChange = (itemId: string, checked: boolean) => {
            const current = Array.isArray(currentValue) ? currentValue : []
            const updated = checked
              ? [...current, itemId]
              : current.filter((id) => id !== itemId)
            bound.handleChange(updated)
          }

          return (
            <div className="space-y-2">
              <Label>
                {control.label}
                {control.required && <span className="ml-1 text-base font-bold text-destructive">*</span>}
              </Label>
              {isLoading ? (
                <p className="text-sm text-muted-foreground">Loading {control.label.toLowerCase()}...</p>
              ) : options.length === 0 ? (
                <p className="text-sm text-muted-foreground">No {control.label.toLowerCase()} available</p>
              ) : (
                <div className="border rounded-md p-3 space-y-3" style={{ maxHeight: '192px', overflowY: 'auto' }}>
                  {options.map((option) => (
                    <div
                      key={option.value}
                      className="flex items-center gap-3 cursor-pointer hover:bg-accent p-2 rounded"
                    >
                      <Checkbox
                        id={`${control.name}-${option.value}`}
                        checked={Array.isArray(currentValue) && currentValue.includes(option.value)}
                        onCheckedChange={(checked) => handleCheckboxChange(option.value, checked === true)}
                      />
                      <label
                        htmlFor={`${control.name}-${option.value}`}
                        className="text-sm font-normal cursor-pointer flex-1"
                      >
                        {option.label}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {bound.state.meta?.errors && bound.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive">{bound.state.meta.errors[0]}</p>
              )}
            </div>
          )
        }}
      </form.AppField>
    )
  }

  // Render control based on type
  const renderControlField = (control: any) => {
    if (control.htmlControl === 'phone') {
      return (
        <form.AppField
          key={control.name}
          name={control.name}
          validators={{
            // Mirror the backend contract (mobileNumber = exactly 10 digits after
            // a 1-3 digit country code) so an invalid number is caught before submit.
            // Validate live (onChange) too — PhoneInputField emits via onChange, so a
            // short/invalid number shows the message immediately instead of staying silent.
            onChange: ({ value }) => validateBackendPhone(value) || undefined,
            onBlur: ({ value }) => validateBackendPhone(value) || undefined,
          }}
        >
          {(bound) => (
            <PhoneInputField
              label={control.label}
              value={bound.state.value as string | undefined}
              onChange={(val) => bound.handleChange(val ?? '')}
              onBlur={bound.handleBlur}
              error={bound.state.meta.errors?.[0] as string | undefined}
            />
          )}
        </form.AppField>
      )
    }
    if (control.htmlControl === 'multiselect') {
      return renderMultiSelectControlField(control)
    }
    if (control.htmlControl === 'file') {
      // Use new MediaUploaderField pattern via renderMediaUploaderControl
      // Enable mockMode in development when backend upload API is not available
      const useMockUpload = config.appEnv === 'development'
      return (
        <form.AppField key={control.name} name={control.name}>
          {(bound) => renderMediaUploaderControl(control, bound, 'new', useMockUpload)}
        </form.AppField>
      )
    }
    if (control.htmlControl === 'select') {
      return renderSelectControlField(control)
    }
    return renderTextControlField(control)
  }

  const formBody = (
    <>
      {/* Form Groups */}
      {groups.map((group) => {
        const groupControls = controlsByGroup.get(group.formGroupName) || []

        return (
          <div key={group.formGroupName} className="space-y-4">
            {groupControls.map(renderControlField)}
          </div>
        )
      })}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && renderButtonControl({
          label: FORM_CONFIG.cancelButton?.label ?? 'Cancel',
          variant: 'outline',
          type: 'button',
          onClick: onCancel,
        })}

        {renderButtonControl({
          label: submitLabel || FORM_CONFIG.submitButton?.label || 'Add User',
          variant: 'default',
          type: 'submit',
          disabled: upsertUser.isPending,
        }, { SubmitButton: form.SubmitButton })}
      </div>
    </>
  )

  return (
    <form.AppForm>
      {showHeader ? (
        <div className="space-y-4">
          <Card className="shadow-sm pb-0 overflow-hidden">
            <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-lg">{FORM_CONFIG.formTitle}</CardTitle>
                  <CardDescription>{FORM_CONFIG.formDescription}</CardDescription>
                </div>
              </div>
            </CardHeader>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="space-y-6 pt-6">{formBody}</CardContent>
          </Card>
        </div>
      ) : (
        <div className="space-y-6">{formBody}</div>
      )}
    </form.AppForm>
  )
}
